import React, {useMemo, useState} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useTranslate} from "../../system/useTranslate";
import Icon from "../../system/components/icon";
import useWorkspaceMembership from "../../models/workspace-membership/use-workspace-membership";
import {selectableWorkspaceMemberships} from "../../models/workspace-membership/workspace-membership-availability";

export default function ForcedWorkspaceSelectionDialog() {
    const translate = useTranslate();
    const navigate = useNavigate();
    const membershipApi = useWorkspaceMembership();
    const {workspaceMembershipsData, workspaceSelectionRequired} = useSelector((state) => state.app);
    const [pendingId, setPendingId] = useState(null);
    const [error, setError] = useState("");
    const memberships = useMemo(
        () => selectableWorkspaceMemberships(workspaceMembershipsData),
        [workspaceMembershipsData],
    );

    const selectWorkspace = async (membership) => {
        const identifier = membership?.documentId || membership?.id;
        if (!identifier || pendingId) return;
        setPendingId(identifier);
        setError("");
        const result = await membershipApi.updateDefaultWorkspaceMembership(identifier);
        if (!result) {
            setError(translate("workspace-selection.error"));
            setPendingId(null);
            return;
        }
        navigate("/");
    };

    const footer = (
        <div className="flex flex-row-reverse align-items-center justify-content-start p-3 surface-50 border-round-bottom-2xl border-top-1 border-300">
            <Button
                type="button"
                className="text-xs gap-2 border-round-lg p-2"
                icon={<Icon icon="mi mi:add_circle" className="text-lg"/>}
                label={translate("create-new-workspace")}
                disabled={Boolean(pendingId)}
                onClick={() => navigate("/workspace/create")}
            />
        </div>
    );

    return (
        <Dialog
            visible={workspaceSelectionRequired}
            closable={false}
            closeOnEscape={false}
            dismissableMask={false}
            className="rtl w-11 md:w-7 lg:w-6 border-round-bottom-2xl"
            header={(
                <div className="flex flex-column gap-1">
                    <span className="text-lg">{translate("workspace-selection.required-title")}</span>
                    <span className="text-xs text-400">{translate("workspace-selection.required-description")}</span>
                </div>
            )}
            headerClassName="border-round-top-2xl border-bottom-1 border-300 pb-3 pt-3"
            footer={footer}
            pt={{
                footer: () => ({className: "p-0 border-round-bottom-2xl"}),
                content: () => ({className: "p-0"}),
            }}
            onHide={() => {}}
        >
            <div className="flex flex-column gap-3 p-4 max-h-25rem overflow-auto">
                {memberships.length ? memberships.map((membership) => {
                    const identifier = membership.documentId || membership.id;
                    return (
                        <div key={identifier} className="flex align-items-center gap-3 border-1 border-200 border-round-xl p-3 surface-card">
                            <span className="w-3rem h-3rem flex align-items-center justify-content-center border-circle surface-100 flex-shrink-0">
                                <Icon icon="mi mi:workspaces" className="text-xl text-primary"/>
                            </span>
                            <span className="flex-1 min-w-0 flex flex-column gap-1">
                                <strong className="text-xs text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                    {membership.workspace?.name}
                                </strong>
                                <small className="text-2xs text-500">{translate("workspace-selection.available")}</small>
                            </span>
                            <Button
                                type="button"
                                className="text-xs gap-2 flex-shrink-0"
                                icon={<Icon icon="mi mi:login"/>}
                                label={translate("workspace-selection.choose")}
                                loading={String(pendingId) === String(identifier)}
                                disabled={Boolean(pendingId)}
                                onClick={() => selectWorkspace(membership)}
                            />
                        </div>
                    );
                }) : (
                    <Message
                        severity="info"
                        className="text-xs justify-content-start"
                        text={translate("workspace-selection.empty")}
                    />
                )}
                {error && <Message severity="error" className="text-xs justify-content-start" text={error}/>}
            </div>
        </Dialog>
    );
}
